<template>
  <div class="set-new-password-modal">
    <div v-if="loadModal" class="modal modal-mask">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header div-responsive">
            <div class="modal-title">
              <h1>Set New Password</h1>
            </div>
            <button type="button" class="close-icon" @click="handleModal($event)" data-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body div-responsive">
            <div class="box-body">
              <div class="box-row">
                <div class="box-col box-col-left">Password</div>
                <div class="box-col box-col-right">
                  <input type="password" :class="hasError && !validatePassword ? 'is-invalid' : ''" v-model="password" placeholder="Password" />
                  <div class="text-danger" v-if="hasError && !validatePassword">
                    <div v-for="(msg, idx) in passwordValidationErrors" style="min-height: 0; line-height: 1" :key="idx">{{ msg }}</div>
                  </div>
                  <div v-if="password" class="mb-4 mt-n2 password-strength-wrap">
                    <div style="margin-top: 5px" class="password-strength-meta">
                      <span>Password strength</span>
                      <span :class="newPasswordStrengthClass">{{ newPasswordStrength }}</span>
                    </div>
                    <v-progress-linear
                        :value="newPasswordStrengthPercent"
                        :color="newPasswordStrengthColor"
                        background-color="#eceff1"
                        height="8"
                        rounded
                    ></v-progress-linear>
                  </div>
                </div>
              </div>
              <div class="box-row">
                <div class="box-col box-col-left">Confirm Password</div>
                <div class="box-col box-col-right">
                  <input type="password" :class="hasError && !validateConfirmPassword ? 'is-invalid' : ''" v-model="confirmPassword" placeholder="Confirm Password" />
                  <div class="text-danger" v-if="hasError && !validateConfirmPassword">New passwords don't match!</div>

                </div>
              </div>
              <div class="row">
                <div class="col">
                  <div class="icon-group right-float-element">
                    <v-btn class="white-button" @click="handleModal($event)" dark>Cancel</v-btn>
                    <v-btn @click="clickSave" dark>Save</v-btn>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import utilities from '@/assets/js/utilities'
  import auth from '@/assets/js/cognitoAuth'
  import { getPasswordPolicyErrors } from '@/assets/js/passwordPolicy'

  export default {
    name: 'SetNewPasswordModal',
    props: [],
    data() {
      return {
        loadModal: false,
        user: {},
        hasError: false,
        password: '',
        confirmPassword: ''
      }
    },

    computed: {
      newPasswordStrengthLevel() {
        const password = this.password || ''
        if (!password) {
          return 0
        }
        if (this.passwordValidationErrors.length > 0) {
          return 1
        }
        const hasRepeatedChars = /(.)\1{2,}/.test(password)
        const hasSequentialPattern = this.hasSequentialPattern(password)
        const hasKeyboardPattern = this.hasKeyboardPattern(password)
        const normalized = this.normalizeForCommonWordCheck(password)
        const hasCommonWordPattern = this.hasCommonWordPattern(normalized)
        const noObviousPattern =
          !hasRepeatedChars &&
          !hasSequentialPattern &&
          !hasKeyboardPattern &&
          !hasCommonWordPattern
        const uniqueCharRatio =
          new Set(password.toLowerCase().split('')).size / password.length

        if (noObviousPattern && uniqueCharRatio >= 0.65) {
          return 4
        }
        if (noObviousPattern) {
          return 3
        }
        return 2
      },
      newPasswordStrength() {
        const level = this.newPasswordStrengthLevel
        if (level <= 1) return 'Weak'
        if (level === 2) return 'Medium'
        if (level === 3) return 'Strong'
        return 'Very Strong'
      },
      newPasswordStrengthClass() {
        if (this.newPasswordStrengthLevel === 4) {
          return 'strength-very-strong'
        }
        if (this.newPasswordStrengthLevel === 3) {
          return 'strength-strong'
        }
        if (this.newPasswordStrengthLevel === 2) {
          return 'strength-medium'
        }
        return 'strength-weak'
      },
      newPasswordStrengthPercent() {
        if (!this.password) {
          return 0
        }
        return this.newPasswordStrengthLevel * 25
      },
      newPasswordStrengthColor() {
        if (this.newPasswordStrengthLevel === 4) {
          return '#0b8043'
        }
        if (this.newPasswordStrengthLevel === 3) {
          return '#1e8e3e'
        }
        if (this.newPasswordStrengthLevel === 2) {
          return '#f29900'
        }
        return '#d93025'
      },
      passwordValidationErrors() {
        return getPasswordPolicyErrors(this.password)
      },
      validatePassword() {
        return this.passwordValidationErrors.length === 0
      },
      validateConfirmPassword() {
          return this.password === this.confirmPassword
      },
    },

    methods: {
      normalizeForCommonWordCheck(value) {
        const substitutions = {
          '0': 'o',
          '1': 'i',
          '3': 'e',
          '4': 'a',
          '5': 's',
          '7': 't',
          '@': 'a',
          '$': 's',
          '!': 'i',
        }
        return (value || '')
          .toLowerCase()
          .split('')
          .map((c) => substitutions[c] || c)
          .join('')
      },
      hasCommonWordPattern(normalizedPassword) {
        const commonWords = [
          'password',
          'welcome',
          'admin',
          'qwerty',
          'abc',
          'iloveyou',
          'letmein',
          'moonlight',
          'sunshine',
          'correcthorse',
          'battery',
        ]
        return commonWords.some((word) => normalizedPassword.includes(word))
      },
      hasKeyboardPattern(password) {
        const lower = (password || '').toLowerCase()
        const patterns = ['qwerty', 'asdfgh', 'zxcvbn']
        return patterns.some((pattern) => lower.includes(pattern))
      },
      hasSequentialPattern(password) {
        if (!password || password.length < 3) return false
        const chars = password.toLowerCase()
        let asc = 1
        let desc = 1
        for (let i = 1; i < chars.length; i += 1) {
          const diff = chars.charCodeAt(i) - chars.charCodeAt(i - 1)
          asc = diff === 1 ? asc + 1 : 1
          desc = diff === -1 ? desc + 1 : 1
          if (asc >= 3 || desc >= 3) {
            return true
          }
        }
        return false
      },
      handleModal(e) {
        this.loadModal = !this.loadModal
        if (this.loadModal) {
          $('html').addClass('modal-mask-open')
        } else {
          $('html').removeClass('modal-mask-open')
        }
      },
      async clickSave() {
          var vm = this
          this.hasError = false
          if(!this.validatePassword || !this.validateConfirmPassword) {
              this.hasError = true
              return
          }

        const pwdSHA = await utilities.logSha1(this.password)
        const res1 = await fetch('https://api.pwnedpasswords.com/range/' + pwdSHA.substring(0, 5))

        const res = await res1.text()
        const formatRes = res.split('\r\n')
        const item = formatRes.find(item => item.indexOf(pwdSHA.substring(5)) > -1)
        if(item) {
          this.$notify({
            dangerouslyUseHTMLString: true,
            message: '<div class="alert-warning">' +
                '            <b>Warning:</b> Your new password has been seen ' + item.split(':')[1] + ' times before, please change it' +
                '          </div>',
          });
          await this.$store.dispatch('loading/setLoadComponent', false)
          return
        }

          auth.setNewPassword(this.user, this.password).then(user=>{
              if (user.challengeName == 'MFA_SETUP') vm.$parent.mfaSetup(user)
              this.handleModal()
          })
      },
    },

    async beforeMount() {},

    mounted() {},
  }
</script>

<style>
.strength-weak {
  color: #d93025;
}

.strength-medium {
  color: #f29900;
}

.strength-strong {
  color: #1e8e3e;
}

.strength-very-strong {
  color: #0b8043;
  font-weight: 600;
}
</style>
